/**
 * OutlineWorkspacePanel - Sleek outline workspace
 *
 * Design: Modern, minimal, professional
 * - Compact header with subtle "Generate" button
 * - Clean card list with consistent spacing
 * - Right-side layout selector panel
 * - Unified with studio design system
 * - Drag-and-drop reordering with dnd-kit
 * - Auto-save with debounce
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { Loader2, Sparkles, Plus, LayoutGrid, Check, Wand2, MessageCircle, ArrowLeft } from 'lucide-react';
import { useMessageStore } from '@/stores/messageStore';
import { useVideoWorkspaceOutline, useVideoWorkspaceStore } from '@/stores/videoWorkspaceStore';
import { useOutlineEditQueueStore } from '@/stores/outlineEditQueueStore';
import { useKubicleAllowed } from '@/hooks/useKubicleAllowed';
import { outlineAPI } from '@/services/api';
import { OutlineSlideCard } from './OutlineSlideCard';
import { LayoutSelectorPanel } from './LayoutSelectorPanel';
import { InsertSlideIndicator } from './InsertSlideIndicator';
import { SmartAddSlideModal, SmartAddSlideData } from './SmartAddSlideModal';
import { BrandingPanel } from './BrandingPanel';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useOutlineSlideStatus } from '@/hooks/useOutlineSlideStatus';
import type { TemplateLayout, SlideOutline } from '@/types/outline';
import { toast } from 'sonner';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface OutlineWorkspacePanelProps {
  conversationId: string;
}

// Sortable wrapper for OutlineSlideCard
interface SortableSlideCardProps {
  slide: SlideOutline;
  index: number;
  totalSlides: number;
  layouts: TemplateLayout[];
  onUpdate: (updates: Partial<SlideOutline>) => void;
  onRemove?: () => void;
  onOpenLayoutSelector: () => void;
  isReadOnly?: boolean;
  generationStatus?: import('@/stores/videoWorkspaceStore').SlideGenerationStatus;
  generatingThinking?: string;
  isAudioFirst?: boolean;
  narrationScript?: string | null;
  hasAudio?: boolean;
  audioDuration?: number | null;
  slideDbId?: string;
  audioVersion?: number | null;
  playingSlideId?: string | null;
  onAudioPlayChange?: (slideId: string, playing: boolean) => void;
  conversationId?: string;
  seamAudioUrl?: string;
}

const SortableSlideCard: React.FC<SortableSlideCardProps> = ({
  slide,
  ...props
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: slide.id });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    // Hide the original item when dragging - the DragOverlay shows the floating copy
    opacity: isDragging ? 0 : 1,
  };

  // Only apply ref and attributes to wrapper, pass listeners to drag handle
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <OutlineSlideCard
        slide={slide}
        isDragging={false}
        dragHandleProps={listeners}
        {...props}
      />
    </div>
  );
};

// Debounce helper
function useDebouncedCallback<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const debouncedCallback = useCallback(
    (...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay]
  ) as T;

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return debouncedCallback;
}

export const OutlineWorkspacePanel: React.FC<OutlineWorkspacePanelProps> = ({
  conversationId,
}) => {
  const {
    outline,
    outlineLoading,
    templateLayouts,
    isOutlineGenerating,
    outlineGeneratingThinking,
    outlineNeedsInput,
    setOutline,
    setTemplateLayouts,
    updateOutlineTitle,
    updateOutlineSlide,
    reorderOutlineSlides,
  } = useVideoWorkspaceOutline();

  // Scene-review layout: a disciplined-compose archetype outline (Kubicle beta, impersonation-aware)
  // OR any audio-first outline (general video — the scene view shows the template layout as intent).
  const kubicleAllowed = useKubicleAllowed();
  const isArchetypeOutline = kubicleAllowed && !!outline?.slides?.some(
    (s) => s.storyboard || (s.visualInstructions || '').includes('[archetype:') || (s.visualInstructions || '').includes('[lessonFrame:'),
  );
  const isStoryboardOutline = isArchetypeOutline || outline?.creationMode === 'audio_first';

  // Track per-slide generation statuses
  const { getSlideStatus } = useOutlineSlideStatus(conversationId, { enabled: !!outline });

  // Narration data from cache (for inline script + audio in slide cards)
  const narrationCache = useVideoWorkspaceStore(state => state.narrationCacheByConversation[conversationId] ?? null);
  const preloadNarrationData = useVideoWorkspaceStore(state => state.preloadNarrationData);

  // Preload narration data when outline is ready so scripts/audio show inline
  useEffect(() => {
    if (outline && !narrationCache) {
      preloadNarrationData(conversationId);
    }
  }, [outline, narrationCache, conversationId, preloadNarrationData]);

  // Audio play state — only one slide plays at a time
  const [playingSlideId, setPlayingSlideId] = useState<string | null>(null);
  const handleAudioPlayChange = useCallback((slideId: string, playing: boolean) => {
    setPlayingSlideId(playing ? slideId : null);
  }, []);

  const [loadingLayouts, setLoadingLayouts] = useState(false);
  const [layoutSelectorOpen, setLayoutSelectorOpen] = useState(false);
  const [selectedSlideForLayout, setSelectedSlideForLayout] = useState<SlideOutline | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [activeId, setActiveId] = useState<string | null>(null);

  // Delete confirmation state
  const [deleteConfirmSlide, setDeleteConfirmSlide] = useState<{ id: string; title: string } | null>(null);

  // Smart add modal state
  const [smartAddModalOpen, setSmartAddModalOpen] = useState(false);
  const [smartAddAfterSlideId, setSmartAddAfterSlideId] = useState<string | undefined>();

  // Track last saved outline for comparison (only set after successful save)
  const lastSavedOutlineRef = useRef<string>('');

  // Keep a ref to the latest outline for use in callbacks
  const outlineRef = useRef(outline);
  outlineRef.current = outline;

  // Blank-canvas/generative mode has no user-selectable layouts — suppress the layout UI
  const isBlankCanvas = outline?.templateId?.startsWith('blank-canvas') || outline?.generationMode === 'generative';
  const effectiveLayouts = isBlankCanvas ? [] : templateLayouts;

  // Configure dnd-kit sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // 8px movement required before drag starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Fetch layouts when component mounts or conversationId changes
  useEffect(() => {
    const fetchLayouts = async () => {
      if (!conversationId || templateLayouts.length > 0) return;

      setLoadingLayouts(true);
      try {
        const response = await outlineAPI.getTemplateLayouts(conversationId);
        if (response.layouts) {
          setTemplateLayouts(response.layouts as TemplateLayout[]);
        }
      } catch (error) {
        console.error('Failed to fetch layouts:', error);
      } finally {
        setLoadingLayouts(false);
      }
    };

    fetchLayouts();
  }, [conversationId, templateLayouts.length, setTemplateLayouts]);

  // Save function ref - always has access to current conversationId
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;

  // Debounced save function - saves title and slides
  const saveOutline = useCallback(async (data: { title?: string; slides?: SlideOutline[] }) => {
    const currentConversationId = conversationIdRef.current;

    if (!currentConversationId || !data.slides) {
      console.log('💾 EARLY RETURN: missing conversationId or slides');
      return;
    }

    // Create a snapshot key to detect changes
    const snapshotKey = JSON.stringify({ title: data.title, slides: data.slides });

    // Skip if nothing changed (but only if we've successfully saved before)
    if (lastSavedOutlineRef.current && snapshotKey === lastSavedOutlineRef.current) {
      console.log('💾 Skipping save: no changes since last successful save');
      return;
    }

    console.log('💾 Saving outline...', {
      slideCount: data.slides?.length,
      slide0Title: data.slides?.[0]?.title,
      slide0Description: data.slides?.[0]?.description?.substring(0, 50),
      slide0KeyPoints: data.slides?.[0]?.keyPoints,
    });

    setSaveStatus('saving');

    try {
      await outlineAPI.update(currentConversationId, data);
      // Only update the snapshot AFTER successful save
      lastSavedOutlineRef.current = snapshotKey;
      // Clear dirty flag - server now has our latest edits
      useVideoWorkspaceStore.getState().setOutlineLocalEditTimestamp(null);
      setSaveStatus('saved');
      console.log('📋 Outline saved successfully');

      // Reset to idle after showing "saved" for 2s
      setTimeout(() => {
        setSaveStatus('idle');
      }, 2000);
    } catch (error) {
      console.error('❌ Failed to save outline:', error);
      setSaveStatus('error');
    }
  }, []); // No dependencies - uses refs

  const debouncedSave = useDebouncedCallback(saveOutline, 1000);

  // Track if there are pending user edits that haven't been saved yet
  // This prevents polling from triggering saves with stale data
  const hasPendingUserEditsRef = useRef(false);

  // Auto-save when outline changes from USER actions only
  // We now manually trigger saves in handleSlideUpdate to avoid race conditions with polling
  // This effect only handles title changes (which don't come through handleSlideUpdate)
  useEffect(() => {
    if (!outline?.slides || !outline?.title) return;
    // Only save if we have pending user edits
    if (!hasPendingUserEditsRef.current) return;

    console.log('🔄 Auto-save effect triggered for title change');
    debouncedSave({ title: outline.title, slides: outline.slides });
    hasPendingUserEditsRef.current = false;
  }, [outline?.title, debouncedSave]);

  // Reset saved snapshot when conversation changes
  useEffect(() => {
    lastSavedOutlineRef.current = '';
  }, [conversationId]);

  // Handle slide updates from OutlineSlideCard
  // This directly triggers save to avoid race conditions with polling
  const handleSlideUpdate = useCallback((slideId: string, updates: Partial<SlideOutline>) => {
    // Get current outline state directly from store to ensure freshness
    const currentOutline = outlineRef.current;
    if (!currentOutline) return;

    // Mark as having pending local edits so polling uses merge instead of replace
    useVideoWorkspaceStore.getState().setOutlineLocalEditTimestamp(Date.now());

    // Update the store first
    updateOutlineSlide(slideId, updates);

    // Immediately trigger save with the updated data
    // Calculate the updated slides manually to avoid stale closure
    const updatedSlides = currentOutline.slides.map((slide) =>
      slide.id === slideId
        ? { ...slide, ...updates, userModified: true }
        : slide
    );

    console.log('📝 handleSlideUpdate: Triggering save for slide', slideId, {
      updatedTitle: updates.title,
      updatedKeyPoints: updates.keyPoints?.length,
    });

    debouncedSave({ title: currentOutline.title, slides: updatedSlides });
  }, [updateOutlineSlide, debouncedSave]);

  // Handle title updates with direct save trigger
  const handleTitleUpdate = useCallback((newTitle: string) => {
    // Mark as having pending local edits so polling uses merge instead of replace
    useVideoWorkspaceStore.getState().setOutlineLocalEditTimestamp(Date.now());
    updateOutlineTitle(newTitle);
    hasPendingUserEditsRef.current = true;
  }, [updateOutlineTitle]);

  const isApproved = outline?.status === 'approved' || outline?.status === 'generating_slides' || outline?.status === 'complete' || outline?.status === 'completed';

  const handleOpenLayoutSelector = (slide: SlideOutline) => {
    setSelectedSlideForLayout(slide);
    setLayoutSelectorOpen(true);
  };

  const handleLayoutSelect = (layoutId: string, layoutName: string, thumbnail?: string) => {
    // Use ref to ensure we have the latest outline (avoid stale closure)
    const currentOutline = outlineRef.current;

    // Mark as having pending local edits so polling uses merge instead of replace
    useVideoWorkspaceStore.getState().setOutlineLocalEditTimestamp(Date.now());

    console.log('📐 handleLayoutSelect called:', { layoutId, layoutName, thumbnail: thumbnail?.substring(0, 50) });
    console.log('📐 selectedSlideForLayout:', selectedSlideForLayout?.id);
    console.log('📐 outline exists:', !!currentOutline);

    if (selectedSlideForLayout && currentOutline) {
      const oldLayoutId = selectedSlideForLayout.layoutId;
      const oldLayoutName = selectedSlideForLayout.layoutName;

      // Update the slide in store
      updateOutlineSlide(selectedSlideForLayout.id, {
        layoutId,
        layoutName,
        layoutThumbnail: thumbnail,
        userModified: true,
      });

      // Immediately save with the updated slide (don't wait for auto-save effect)
      const updatedSlides = currentOutline.slides.map((slide) =>
        slide.id === selectedSlideForLayout.id
          ? { ...slide, layoutId, layoutName, layoutThumbnail: thumbnail, userModified: true }
          : slide
      );
      console.log('📐 Calling debouncedSave with updatedSlides count:', updatedSlides.length);
      debouncedSave({ title: currentOutline.title, slides: updatedSlides });

      // Queue the change for AI processing (only if layout actually changed)
      if (oldLayoutId !== layoutId) {
        const { addOutlineChange } = useVideoWorkspaceStore.getState();
        addOutlineChange(conversationId, {
          id: crypto.randomUUID(),
          conversationId,
          slideId: selectedSlideForLayout.id,
          slideTitle: selectedSlideForLayout.title,
          changeType: 'layout_change',
          description: `Layout: ${oldLayoutName} \u2192 ${layoutName}`,
          details: {
            field: 'layoutId',
            oldValue: oldLayoutId,
            newValue: layoutId,
            oldDisplayName: oldLayoutName,
            newDisplayName: layoutName,
          },
          timestamp: Date.now(),
        });

        // Open the chat drawer so the user sees the queued change
        useVideoWorkspaceStore.getState().setLeftPanelCollapsed(false);
      }
    } else {
      console.log('📐 SKIPPED: selectedSlideForLayout or outline is null');
    }
  };

  // Drag and drop handlers
  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (over && active.id !== over.id && outline) {
      // Get active slides only (exclude deleted)
      const activeSlides = outline.slides.filter(s => !s.isDeleted);
      const oldIndex = activeSlides.findIndex((s) => s.id === active.id);
      const newIndex = activeSlides.findIndex((s) => s.id === over.id);

      if (oldIndex !== -1 && newIndex !== -1) {
        // Use the API-based reorder which handles renumbering server-side
        handleReorderSlides(oldIndex, newIndex);
      }
    }
  };

  const activeSlide = activeId ? outline?.slides.find((s) => s.id === activeId) : null;

  // ============================================================================
  // SLIDE MANAGEMENT: Add, Delete with server-side renumbering
  // ============================================================================

  // Get conversation-scoped setter
  const { setOutlineForConversation } = useVideoWorkspaceStore.getState();

  // Handler to open the smart add modal
  const handleOpenSmartAdd = useCallback((afterSlideId?: string) => {
    setSmartAddAfterSlideId(afterSlideId);
    setSmartAddModalOpen(true);
  }, []);

  // Handler for smart add submission - queues add_slide change for AI
  const handleSmartAddSubmit = useCallback(async (data: SmartAddSlideData) => {
    if (!conversationId) return;

    const truncatedInstructions = data.instructions.length > 50
      ? data.instructions.slice(0, 50) + '...'
      : data.instructions;

    const { addOutlineChange } = useVideoWorkspaceStore.getState();
    addOutlineChange(conversationId, {
      id: crypto.randomUUID(),
      conversationId,
      slideId: 'new-' + crypto.randomUUID(),
      slideTitle: truncatedInstructions || 'New Slide',
      changeType: 'add_slide',
      description: `Add slide: ${truncatedInstructions}`,
      details: {
        instructions: data.instructions,
        layoutId: data.layoutId || undefined,
        layoutName: data.layoutName || undefined,
        afterSlideId: smartAddAfterSlideId || undefined,
      },
      timestamp: Date.now(),
    });

    // Close modal and open chat drawer
    setSmartAddModalOpen(false);
    useVideoWorkspaceStore.getState().setLeftPanelCollapsed(false);
  }, [conversationId, smartAddAfterSlideId]);

  // Handler to show delete confirmation
  const handleRequestDelete = useCallback((slideId: string, slideTitle: string) => {
    setDeleteConfirmSlide({ id: slideId, title: slideTitle });
  }, []);

  // Handler to queue a slide deletion for AI processing
  const handleRemoveSlide = useCallback((slideId: string) => {
    if (!conversationId || !outline) return;

    const slide = outline.slides.find(s => s.id === slideId);
    const slideTitle = slide?.title || slideId;

    const { addOutlineChange } = useVideoWorkspaceStore.getState();
    addOutlineChange(conversationId, {
      id: crypto.randomUUID(),
      conversationId,
      slideId,
      slideTitle,
      changeType: 'delete_slide',
      description: 'Delete slide',
      details: {},
      timestamp: Date.now(),
    });

    // Open chat drawer so user sees the queued change
    useVideoWorkspaceStore.getState().setLeftPanelCollapsed(false);
  }, [conversationId, outline]);

  // Confirm delete handler
  const handleConfirmDelete = useCallback(() => {
    if (!deleteConfirmSlide) return;
    handleRemoveSlide(deleteConfirmSlide.id);
    setDeleteConfirmSlide(null);
  }, [deleteConfirmSlide, handleRemoveSlide]);

  // Handler to reorder slides via API (with server-side renumbering)
  const handleReorderSlides = useCallback(async (fromIndex: number, toIndex: number) => {
    if (!conversationId || !outline) return;

    try {
      // Optimistic update first
      reorderOutlineSlides(fromIndex, toIndex);

      // Then sync with server
      const result = await outlineAPI.reorderSlides(conversationId, { fromIndex, toIndex });

      // Update store with server response (has renumbered IDs)
      setOutlineForConversation(conversationId, result.outline);

      // Storyboard/disciplined-compose: queue a flow re-plan instruction reflecting the new order
      // (the order is already persisted; the agent adjusts chapter flow / transitions / continuity).
      const ordered = (result.outline.slides || []).filter((s) => !s.isDeleted);
      if (kubicleAllowed && ordered.some((s) => s.storyboard)) {
        const orderList = ordered.map((s, i) => `${i + 1}. ${s.title || s.id}`).join('; ');
        useOutlineEditQueueStore.getState().enqueue(conversationId, {
          kind: 'reorder',
          summary: `Reorder scenes (${ordered.length})`,
          message:
            `The scenes were reordered. New order: ${orderList}. ` +
            `Re-plan the chapter flow, transitions, and narration continuity to match this order, and update the outline.`,
          dedupeKey: 'reorder',
        });
      }
    } catch (error) {
      console.error('Failed to reorder slides:', error);
      toast.error('Failed to reorder slides');
      // Revert on error by refetching outline
      try {
        const refetchedOutline = await outlineAPI.get(conversationId);
        if (refetchedOutline) {
          setOutlineForConversation(conversationId, refetchedOutline);
        }
      } catch (refetchError) {
        console.error('Failed to refetch outline:', refetchError);
      }
    }
  }, [conversationId, outline, reorderOutlineSlides, setOutlineForConversation, kubicleAllowed]);

  // Loading state
  if (outlineLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center studio-canvas">
        <div className="flex flex-col items-center gap-3">
          <div className="relative">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-primary/20">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          </div>
          <p className="text-sm text-gray-500 dark:text-white/40">Loading outline...</p>
        </div>
      </div>
    );
  }

  // Generating outline state - show AI thinking progress
  if (isOutlineGenerating && !outline) {
    return (
      <div className="h-full w-full flex items-center justify-center studio-canvas">
        <div className="flex flex-col items-center gap-4 md:gap-6 text-center max-w-md px-4 md:px-6">
          {/* Animated AI Icon */}
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl flex items-center justify-center bg-primary/20">
              <Wand2 className="w-8 h-8 text-primary animate-pulse" />
            </div>
            {/* Animated ring */}
            <div className="absolute inset-0 rounded-2xl border-2 border-primary/30 animate-ping" style={{ animationDuration: '2s' }} />
          </div>

          {/* Title */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Creating your outline
            </h3>
            <p className="text-sm text-gray-500 dark:text-white/50">
              AI is analyzing your request and designing the perfect video structure
            </p>
          </div>

          {/* Thinking steps indicator */}
          {outlineGeneratingThinking && (
            <div className="w-full">
              <div className="rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-4 h-4 text-primary animate-pulse flex-shrink-0" />
                  <span className="text-sm text-primary dark:text-accent-foreground truncate">
                    {outlineGeneratingThinking}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Progress dots */}
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 rounded-full bg-primary animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  // Outline generation ended without producing an outline - show AI's response
  if (outlineNeedsInput && !outline) {
    // Get the last assistant message from the message store
    const messages = useMessageStore.getState().getMessagesForConversation(conversationId);
    const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant');
    const messageText = lastAssistantMessage?.content || 'The AI responded without generating an outline. Please provide more details or try a different format.';

    return (
      <div className="h-full w-full flex items-center justify-center studio-canvas">
        <div className="flex flex-col items-center gap-4 md:gap-6 text-center max-w-md px-4 md:px-6">
          {/* Icon */}
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 flex items-center justify-center">
            <MessageCircle className="w-8 h-8 text-amber-500 dark:text-amber-400" />
          </div>

          {/* Title */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              More information needed
            </h3>
            <p className="text-sm text-gray-500 dark:text-white/50">
              The AI needs additional details to create your outline
            </p>
          </div>

          {/* AI message card */}
          <div className="w-full rounded-xl border border-amber-500/20 bg-amber-500/5 dark:bg-amber-500/10 px-4 py-3 text-left">
            <p className="text-sm text-gray-700 dark:text-white/70 line-clamp-6 whitespace-pre-wrap">
              {messageText}
            </p>
          </div>

          {/* Reply in chat link */}
          <button
            onClick={() => {
              useVideoWorkspaceStore.getState().setLeftPanelCollapsed(false);
            }}
            className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Reply in chat</span>
          </button>
        </div>
      </div>
    );
  }

  // No outline yet state
  if (!outline) {
    return (
      <div className="h-full w-full flex items-center justify-center studio-canvas">
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-white/[0.04] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center">
            <LayoutGrid className="w-6 h-6 text-gray-400 dark:text-white/30" />
          </div>
          <div>
            <h3 className="text-sm font-medium text-gray-700 dark:text-white/70 mb-1">No outline yet</h3>
            <p className="text-xs text-gray-500 dark:text-white/40 max-w-[200px]">
              Start a conversation to generate your video outline
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full flex flex-col overflow-hidden studio-canvas">
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto">
        <div className={`${isStoryboardOutline ? 'max-w-5xl' : 'max-w-3xl'} mx-auto px-3 md:px-6 py-4 md:py-6`}>
          {/* Video Title */}
          <div className="mb-4 md:mb-6">
            <input
              type="text"
              value={outline.title || ''}
              onChange={(e) => handleTitleUpdate(e.target.value)}
              disabled={isApproved}
              placeholder="Video title..."
              className={cn(
                "w-full text-lg md:text-2xl font-semibold bg-transparent outline-none",
                "text-gray-900 dark:text-white",
                "placeholder:text-gray-300 dark:placeholder:text-white/30",
                "focus:placeholder:text-gray-400 dark:focus:placeholder:text-white/40",
                "transition-colors",
                isApproved && "cursor-default"
              )}
            />
          </div>

          {/* Compact Header */}
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4 md:mb-6">
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-gray-700 dark:text-white/70">
                  {outline.slides.filter(s => !s.isDeleted).length} slides
                </span>
              </div>
              <span className="text-gray-300 dark:text-white/20">•</span>
              <span className="text-sm text-gray-500 dark:text-white/40">{outline.templateName}</span>

              {/* Save status indicator */}
              {saveStatus !== 'idle' && (
                <>
                  <span className="text-gray-300 dark:text-white/20">•</span>
                  <div className={cn(
                    "flex items-center gap-1.5 text-xs",
                    saveStatus === 'saving' && "text-amber-500 dark:text-amber-400/70",
                    saveStatus === 'saved' && "text-emerald-500 dark:text-emerald-400/70",
                    saveStatus === 'error' && "text-red-500 dark:text-red-400/70"
                  )}>
                    {saveStatus === 'saving' && (
                      <>
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Saving...</span>
                      </>
                    )}
                    {saveStatus === 'saved' && (
                      <>
                        <Check className="w-3 h-3" />
                        <span>Saved</span>
                      </>
                    )}
                    {saveStatus === 'error' && (
                      <span>Save failed</span>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Company Branding Panel */}
          <BrandingPanel
            conversationId={conversationId}
            branding={outline.branding}
            onBrandingChange={async (branding) => {
              // Mark as having pending local edits so SSE refetch uses merge instead of replace
              useVideoWorkspaceStore.getState().setOutlineLocalEditTimestamp(Date.now());

              // Optimistic update - immediately update local state
              const previousBranding = outline.branding;
              setOutline({ ...outline, branding });

              try {
                const result = await outlineAPI.updateBranding(conversationId, branding);
                // Re-apply branding from API response to current outline
                // (SSE may have refetched and overwritten our optimistic update)
                const currentOutline = useVideoWorkspaceStore.getState().outline;
                if (currentOutline && result.branding) {
                  setOutline({ ...currentOutline, branding: result.branding });
                }
              } catch (error) {
                // Revert on failure - re-apply previous branding to current outline
                console.error('Failed to update branding:', error);
                const currentOutline = useVideoWorkspaceStore.getState().outline;
                if (currentOutline) {
                  setOutline({ ...currentOutline, branding: previousBranding });
                }
                toast.error('Failed to update branding');
              }
            }}
            onBrandingClear={async () => {
              // Mark as having pending local edits so SSE refetch uses merge instead of replace
              useVideoWorkspaceStore.getState().setOutlineLocalEditTimestamp(Date.now());

              // Optimistic update - immediately clear branding
              const previousBranding = outline.branding;
              setOutline({ ...outline, branding: undefined });

              try {
                await outlineAPI.clearBranding(conversationId);
                // Re-apply cleared branding to current outline
                // (SSE may have refetched and overwritten our optimistic update)
                const currentOutline = useVideoWorkspaceStore.getState().outline;
                if (currentOutline) {
                  setOutline({ ...currentOutline, branding: undefined });
                }
                toast.success('Branding reset to template defaults');
              } catch (error) {
                // Revert on failure - re-apply previous branding to current outline
                console.error('Failed to clear branding:', error);
                const currentOutline = useVideoWorkspaceStore.getState().outline;
                if (currentOutline) {
                  setOutline({ ...currentOutline, branding: previousBranding });
                }
                toast.error('Failed to clear branding');
                throw error;
              }
            }}
            onLogoUpload={async (file) => {
              try {
                const result = await outlineAPI.uploadLogo(conversationId, file);
                // Update current outline with the new logo from API response
                // (use current state in case SSE updated the outline)
                const currentOutline = useVideoWorkspaceStore.getState().outline;
                if (currentOutline && result.logo) {
                  setOutline({
                    ...currentOutline,
                    branding: {
                      ...currentOutline.branding,
                      colors: currentOutline.branding?.colors || { primary: '#000000', secondary: '#333333' },
                      logo: result.logo,
                    },
                  });
                }
              } catch (error) {
                console.error('Failed to upload logo:', error);
                toast.error('Failed to upload logo');
                throw error;
              }
            }}
            onLogoDelete={async () => {
              // Optimistic update - immediately remove logo
              const previousBranding = outline.branding;
              if (outline.branding) {
                setOutline({
                  ...outline,
                  branding: { ...outline.branding, logo: undefined },
                });
              }

              try {
                await outlineAPI.deleteLogo(conversationId);
                // Re-apply logo deletion to current outline
                // (SSE may have refetched and overwritten our optimistic update)
                const currentOutline = useVideoWorkspaceStore.getState().outline;
                if (currentOutline?.branding) {
                  setOutline({
                    ...currentOutline,
                    branding: { ...currentOutline.branding, logo: undefined },
                  });
                }
              } catch (error) {
                // Revert on failure - re-apply previous branding to current outline
                console.error('Failed to delete logo:', error);
                const currentOutline = useVideoWorkspaceStore.getState().outline;
                if (currentOutline) {
                  setOutline({ ...currentOutline, branding: previousBranding });
                }
                toast.error('Failed to delete logo');
                throw error;
              }
            }}
          />

          {/* Slide Cards with Drag and Drop */}
          {(() => {
            // Filter out deleted slides for display
            const activeSlides = outline.slides.filter(s => !s.isDeleted);
            return (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeSlides.map((s) => s.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-0">
                {activeSlides.map((slide, index) => {
                  // Get status from hook or from outline itself
                  const statusInfo = getSlideStatus(slide.id);
                  const generationStatus: import('@/stores/videoWorkspaceStore').SlideGenerationStatus =
                    statusInfo?.status ||
                    (slide.generationStatus as import('@/stores/videoWorkspaceStore').SlideGenerationStatus) ||
                    'pending';
                  const generatingThinking = statusInfo?.generatingThinking || slide.generatingThinking;

                  // Count active (non-deleted) slides
                  const activeSlidesCount = activeSlides.length;

                  // Look up narration script + audio for this slide from cache
                  const slideScript = narrationCache?.scripts?.find(s => s.slideIdentifier === slide.id);
                  const slideAudio = narrationCache?.audioStatus?.find(s => s.slideIdentifier === slide.id);

                  return (
                    <React.Fragment key={slide.id}>
                      {/* Insert indicator before first slide */}
                      {index === 0 && (
                        <InsertSlideIndicator
                          onInsert={() => handleOpenSmartAdd()}
                          orientation="vertical"
                          className="py-2"
                        />
                      )}

                      <SortableSlideCard
                        slide={slide}
                        index={index}
                        totalSlides={activeSlidesCount}
                        conversationId={conversationId}
                        layouts={effectiveLayouts}
                        onUpdate={(updates) => handleSlideUpdate(slide.id, updates)}
                        onRemove={activeSlidesCount > 1 ? () => handleRequestDelete(slide.id, slide.title) : undefined}
                        onOpenLayoutSelector={() => handleOpenLayoutSelector(slide)}
                        generationStatus={generationStatus}
                        generatingThinking={generatingThinking}
                        isAudioFirst={outline.creationMode === 'audio_first'}
                        narrationScript={slideScript?.script?.scriptText}
                        hasAudio={slideAudio?.hasAudio}
                        audioDuration={slideAudio?.audioDuration}
                        slideDbId={slideAudio?.slideId}
                        audioVersion={slideAudio?.version}
                        playingSlideId={playingSlideId}
                        onAudioPlayChange={handleAudioPlayChange}
                        seamAudioUrl={outline.seamAudioUrl}
                      />

                      {/* Insert indicator after each slide */}
                      <InsertSlideIndicator
                        onInsert={() => handleOpenSmartAdd(slide.id)}
                        orientation="vertical"
                        className="py-2"
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </SortableContext>

            {/* Drag overlay for better visual feedback */}
            <DragOverlay>
              {activeSlide ? (
                <div className="opacity-80">
                  <OutlineSlideCard
                    slide={activeSlide}
                    index={activeSlides.findIndex((s) => s.id === activeSlide.id)}
                    totalSlides={activeSlides.length}
                    layouts={effectiveLayouts}
                    onUpdate={() => {}}
                    onOpenLayoutSelector={() => {}}
                    isReadOnly={true}
                    isDragging={true}
                  />
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
            );
          })()}

          {/* Add Slide Button */}
          <button
            onClick={() => handleOpenSmartAdd()}
            className={cn(
              "w-full mt-3 py-3 rounded-xl",
              "border border-dashed border-gray-200 dark:border-white/[0.08]",
              "flex items-center justify-center gap-2",
              "text-xs font-medium text-gray-400 dark:text-white/30",
              "hover:border-gray-300 dark:hover:border-white/20",
              "hover:text-gray-500 dark:hover:text-white/50",
              "hover:bg-gray-50 dark:hover:bg-white/[0.02]",
              "transition-all duration-200"
            )}
          >
            <Plus className="w-4 h-4" />
            <span>Add slide</span>
          </button>

          {/* Bottom padding */}
          <div className="h-6" />
        </div>
      </div>

      {/* Loading layouts indicator */}
      {loadingLayouts && (
        <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-lg bg-white dark:bg-zinc-900/80 backdrop-blur border border-gray-200 dark:border-white/[0.06] shadow-sm">
          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-white/40">
            <Loader2 className="w-3 h-3 animate-spin" />
            <span>Loading layouts...</span>
          </div>
        </div>
      )}

      {/* Layout Selector Panel */}
      {selectedSlideForLayout && (
        <LayoutSelectorPanel
          isOpen={layoutSelectorOpen}
          onClose={() => {
            setLayoutSelectorOpen(false);
            setSelectedSlideForLayout(null);
          }}
          slide={selectedSlideForLayout}
          conversationId={conversationId}
          layouts={effectiveLayouts}
          onLayoutSelect={handleLayoutSelect}
        />
      )}

      {/* Smart Add Slide Modal */}
      <SmartAddSlideModal
        open={smartAddModalOpen}
        onOpenChange={setSmartAddModalOpen}
        layouts={effectiveLayouts}
        onSubmit={handleSmartAddSubmit}
        isSubmitting={false}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={!!deleteConfirmSlide}
        onOpenChange={(open) => {
          if (!open) setDeleteConfirmSlide(null);
        }}
        title="Delete Slide?"
        description={
          <span>
            Are you sure you want to delete <strong>"{deleteConfirmSlide?.title || 'this slide'}"</strong>?
            <br />
            <span className="text-gray-500 dark:text-zinc-400">You can undo this action for a few seconds after deletion.</span>
          </span>
        }
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
};
